import { ProcessEntity } from '@common/enums/processEntity';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type { AfwijkingOption } from '../../../../../../common/customControls/tooV1Afwijking';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IDiagnosis } from '../../../../interfaces/diagnosis';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import type { ICustomControlData } from './TooV1DiagnosticReportAddEditCustomControlFields';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { afwijkingWriteOptions } from '../../../../../../common/customControls/tooV1Afwijking';
import { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { TooV1DiagnosticReportCustomControlDataConclusieOption } from '../../../../../../common/validation/customControls/ophthalmology';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { emptyValidationErrors } from '../../../../../utils/validation';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ReloadEpisode } from '../../../episodes/reloadEpisode';
import { TooV1DiagnosticReportAddEditCustomControlFields } from './TooV1DiagnosticReportAddEditCustomControlFields';

interface IInnerTooV1DiagnosticReportAddEditState {
    buttonDisabled?: boolean;
}

export enum TooV1CustomControlFields {
    afwijking_in_ao = 'afwijking_in_ao',
    afwijking_in_vo = 'afwijking_in_vo',
    optometrische_diagnose = 'optometrische_diagnose',
}

export function TooV1DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    return <InnerTooV1DiagnosticReportAddEdit {...props} />;
}

const afwijkingVoOptions: AfwijkingOption[] = ['oogleden', 'conjunctiva', 'cornea', 'vok', 'iris', 'lens'];
const afwijkingAoOptions: AfwijkingOption[] = ['glasvocht', 'perifere_retina', 'papil', 'macula'];

class InnerTooV1DiagnosticReportAddEdit extends Component<
    ICustomControlDiagnosticReportAddEditProps,
    IInnerTooV1DiagnosticReportAddEditState
> {
    public render() {
        return (
            <ProcessContainer isActiveActivity={this.props.itemState?.initialIsOpen}>
                <ProcessItemHeader
                    isFirstItem={this.props.isFirstItem}
                    isItemOpen
                    organizationName={this.props.currentDiagnosticReport?.performerOrganization?.name}
                    practitionerName={
                        this.props.currentDiagnosticReport && this.props.currentDiagnosticReport.performerPractitioner
                            ? getDisplayName(this.props.currentDiagnosticReport.performerPractitioner)
                            : undefined
                    }
                    process={this.props.process}
                    title={getServiceLabel(
                        this.props.service.code,
                        'diagnosticReport',
                        translateKey('diagnosticReport.findings')
                    )}
                />
                <Form onSubmit={() => null}>
                    <TooV1DiagnosticReportAddEditCustomControlFields
                        {...this.props}
                        customControlData={this.props.diagnosticReport.customControlData}
                    />

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.diagnosticReport}
                                isExisting={!!this.props.request.id}
                                revertChanges={this.props.revertDiagnosticReportChanges}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveDiagnosticReport('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled}
                                saveSend={async () => {
                                    await this.saveDiagnosticReport('final');
                                }}
                            />,
                        ]}
                    />
                </Form>
            </ProcessContainer>
        );
    }

    private async saveDiagnosticReport(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedDiagnosticReport = await this.props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        const diagnosticReportId = await this.props.onSaveDiagnosticReport(saveType);

        if (saveType === 'draft') {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'diagnosticReport',
                translateKey('toast.draftDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'diagnosticReport',
                translateKey('toast.submitDefaultMessage')
            );
        }

        await this.setState({ buttonDisabled: false });
        await this.props.onSave(diagnosticReportId);

        if (saveType === 'final') {
            this.props.reloadProcess(ReloadEpisode.reloadAll);
        }
    }
}

export function tooV1DiagnosticReportValidateOneCustomControlData(
    name: string,
    customControlData: any,
    errors: ValidationErrors,
    diagnosis?: IDiagnosis
) {
    switch (name) {
        case TooV1CustomControlFields.afwijking_in_vo:
            return validateErrorsForAfwijking(
                errors,
                customControlData.afwijking_in_vo,
                customControlData.conclusie_vo,
                afwijkingVoOptions,
                TooV1CustomControlFields.afwijking_in_vo
            );
        case TooV1CustomControlFields.afwijking_in_ao:
            return validateErrorsForAfwijking(
                errors,
                customControlData.afwijking_in_ao,
                customControlData.conclusie_ao,
                afwijkingAoOptions,
                TooV1CustomControlFields.afwijking_in_ao
            );
        case TooV1CustomControlFields.optometrische_diagnose:
            return validateErrorsForOptometrischeDiagnose(customControlData, errors, diagnosis);
        default:
            return errors;
    }
}

export function tooV1DiagnosticReportValidateCustomControlData(
    customControlData: any,
    errors: ValidationErrors,
    diagnosis?: IDiagnosis
) {
    errors = validateErrorsForAfwijking(
        errors,
        customControlData.afwijking_in_vo,
        customControlData.conclusie_vo,
        afwijkingVoOptions,
        TooV1CustomControlFields.afwijking_in_vo
    );
    errors = validateErrorsForAfwijking(
        errors,
        customControlData.afwijking_in_ao,
        customControlData.conclusie_ao,
        afwijkingAoOptions,
        TooV1CustomControlFields.afwijking_in_ao
    );
    errors = validateErrorsForOptometrischeDiagnose(customControlData, errors, diagnosis);

    return errors;
}

function areOptionsFilled(afwijking: any, name: AfwijkingOption, options: { key: string }[]) {
    const isAndersChecked: boolean = afwijking[`${name}Od_andersCheck`] || afwijking[`${name}Os_andersCheck`];
    const isAndersValid: boolean = afwijking[`${name}AndersText`] !== null;
    if (isAndersChecked && !isAndersValid) {
        return false;
    }

    return options.some((option) => {
        if (option.key === 'andersText') {
            return isAndersChecked && isAndersValid;
        } else {
            return afwijking[`${name}Od_${option.key}`] || afwijking[`${name}Os_${option.key}`];
        }
    });
}

function isAfwijkingEmpty(afwijking: any, options: AfwijkingOption[]) {
    const optionsFilled = options.map((option) => areOptionsFilled(afwijking, option, afwijkingWriteOptions[option]));

    return optionsFilled.every((bool) => !bool);
}

function getAfwijkingErrors(
    afwijking: any,
    key: TooV1CustomControlFields.afwijking_in_vo | TooV1CustomControlFields.afwijking_in_ao,
    options: AfwijkingOption[]
) {
    let errors = emptyValidationErrors;

    const optionsFilled = options.map(
        (option) => afwijking[option] && areOptionsFilled(afwijking, option, afwijkingWriteOptions[option])
    );

    for (const [index, value] of options.entries()) {
        if (afwijking[value] && !optionsFilled[index]) {
            errors = errors.set(`customControlData.${key}.${value}`, 'any_required');
        }
    }

    return errors;
}

function validateErrorsForAfwijking(
    errorsForBevindingen: ValidationErrors,
    afwijking: any,
    conclusie: TooV1DiagnosticReportCustomControlDataConclusieOption,
    options: AfwijkingOption[],
    afwijkingKey: TooV1CustomControlFields.afwijking_in_vo | TooV1CustomControlFields.afwijking_in_ao
) {
    let validationErrors = errorsForBevindingen;

    if (conclusie === TooV1DiagnosticReportCustomControlDataConclusieOption.different) {
        const errors = getAfwijkingErrors(afwijking, afwijkingKey, options);

        for (const option of options) {
            const errorKey = `diagnosticReport.customControlData.${afwijkingKey}.${option}`;
            if (errors.get(errorKey)) {
                validationErrors = validationErrors.set(errorKey, 'any_required');
            } else {
                validationErrors = validationErrors.remove(errorKey);
            }
        }

        if (errors.size === 0 && isAfwijkingEmpty(afwijking, options)) {
            validationErrors = validationErrors.set(
                `diagnosticReport.customControlData.${afwijkingKey}`,
                'any_required'
            );
        } else {
            validationErrors = validationErrors.remove(`diagnosticReport.customControlData.${afwijkingKey}`);
        }
    }

    return validationErrors;
}

function validateErrorsForOptometrischeDiagnose(
    customControlData: ICustomControlData,
    errorsForBevindingen: ValidationErrors,
    diagnosis?: IDiagnosis
) {
    if (
        customControlData.conclusie_diagnose === ConclusieDiagnoseOption.nog_geen_diagnose ||
        customControlData.conclusie_diagnose === ConclusieDiagnoseOption.geen_afwijkingen
    ) {
        return errorsForBevindingen.remove('diagnosticReport.customControlData.optometrische_diagnose');
    }

    const optometrische_diagnose = customControlData.optometrische_diagnose;
    const optometrischeDiagnoseFilledIn = Object.keys(optometrische_diagnose).some(
        (item) =>
            optometrische_diagnose[item] !== false ||
            diagnosis?.diagnosisConsiderationIcd10 ||
            diagnosis?.diagnosisConsiderationIcd10fallback
    );

    if (!optometrischeDiagnoseFilledIn) {
        return errorsForBevindingen.set('diagnosticReport.customControlData.optometrische_diagnose', 'any_required');
    }

    return errorsForBevindingen.remove('diagnosticReport.customControlData.optometrische_diagnose');
}
